/**
 * HUB-H — Données équipe / audit pour onglet Centre Activité (Carlos-only).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  computeTeamActivity,
  isInDateRange,
  matchesAuditSearch,
  matchesSecuritySearch,
} from '../../utils/equipe/equipeDisplay';

export default function useActiviteEquipe(api, filters, { enabled = true } = {}) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [securityDashboard, setSecurityDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('');

  const fetchAll = useCallback(async () => {
    if (!api || !enabled) return;
    setLoading(true);
    setError(null);

    const logParams = new URLSearchParams({ page: '1', limit: '40' });
    if (filters.date_debut) logParams.append('date_debut', filters.date_debut);
    if (filters.date_fin) logParams.append('date_fin', filters.date_fin);
    if (severityFilter) logParams.append('severity', severityFilter);
    if (filters.search) logParams.append('username', filters.search);

    try {
      const results = await Promise.allSettled([
        api.get(`/audit/log?${logParams.toString()}`),
        api.get('/audit/stats?days=30'),
        api.get('/audit/fraud-alerts'),
        api.get('/security/events?limit=25&page=1'),
        api.get('/security/dashboard'),
      ]);

      const get = (idx, fallback) => (results[idx]?.status === 'fulfilled' ? results[idx].value.data : fallback);

      setLogs(get(0, {})?.logs || []);
      setStats(get(1, null));
      setAlerts(get(2, {})?.alerts || []);
      setSecurityEvents(get(3, {})?.events || []);
      setSecurityDashboard(get(4, null));

      const allFailed = results.every((r) => r.status === 'rejected');
      if (allFailed) {
        setError('Accès audit refusé ou indisponible');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement équipe');
      setLogs([]);
      setStats(null);
      setAlerts([]);
      setSecurityEvents([]);
      setSecurityDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [api, enabled, filters.date_debut, filters.date_fin, filters.search, severityFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredLogs = useMemo(
    () => logs.filter((log) => matchesAuditSearch(log, filters.search)),
    [logs, filters.search],
  );

  const filteredSecurity = useMemo(
    () => securityEvents.filter(
      (e) => isInDateRange(e.created_at || e.timestamp, filters.date_debut, filters.date_fin)
        && matchesSecuritySearch(e, filters.search),
    ),
    [securityEvents, filters.date_debut, filters.date_fin, filters.search],
  );

  const recentLogs = useMemo(
    () => [...filteredLogs].slice(0, 20),
    [filteredLogs],
  );

  const criticalLogs = useMemo(
    () => filteredLogs.filter((l) => l.severity === 'critical'),
    [filteredLogs],
  );

  const teamActivity = useMemo(
    () => computeTeamActivity(filteredLogs),
    [filteredLogs],
  );

  const kpis = useMemo(() => ({
    totalLogs: filteredLogs.length,
    critical: filteredLogs.filter((l) => l.severity === 'critical').length,
    warning: filteredLogs.filter((l) => l.severity === 'warning').length,
    info: filteredLogs.filter((l) => l.severity === 'info').length,
    alerts: alerts.length,
    securityEvents: filteredSecurity.length,
    statsTotal: stats?.total ?? 0,
    activeBans: securityDashboard?.active_bans_count ?? 0,
    failedAttempts: securityDashboard?.failed_attempts_count ?? 0,
  }), [filteredLogs, alerts, filteredSecurity, stats, securityDashboard]);

  return {
    logs: filteredLogs,
    recentLogs,
    criticalLogs,
    alerts,
    securityEvents: filteredSecurity,
    teamActivity,
    stats,
    kpis,
    loading,
    error,
    severityFilter,
    setSeverityFilter,
    refresh: fetchAll,
  };
}
